import { type ResultOf, useSession } from '@horof/shared';
import { useQuery } from 'urql';
import { graphql } from './gql';

/**
 * The phone app is the treasurer's (doc 38): balances, an income or expense
 * with its receipt, a deposit or withdrawal at the bank, his recent entries.
 * Everything else is done on the desk (docs 30–37).
 */

/** Projects, accounts with their balance, and the receipt rule's amount. */
export const FinanceListsDocument = graphql(`
  query FinanceLists {
    projects(orderBy: [NAME_ASC]) {
      nodes {
        id
        name
        status
      }
    }
    financialAccounts(orderBy: [NAME_ASC]) {
      nodes {
        id
        name
        type
        currentBalance
      }
    }
    currentSettings {
      receiptPhotoMinAmount
    }
  }
`);

export type FinanceLists = ResultOf<typeof FinanceListsDocument>;
export type Account = NonNullable<
  NonNullable<FinanceLists['financialAccounts']>['nodes'][number]
>;

/**
 * Refetch after any change: balances are computed by arqam from the
 * operations, and urql refreshes a cached query only for the types it saw.
 */
export const withBalances = {
  additionalTypenames: [
    'FinancialOperation',
    'AccountTransfer',
    'FinancialAccount',
    'Project',
  ],
};

/** The lists, kept fresh. */
export function useFinanceLists() {
  return useQuery({ query: FinanceListsDocument, context: withBalances });
}

/** What a row of the list shows. */
export const OperationRowFragment = graphql(`
  fragment OperationRow on FinancialOperation {
    id
    date
    type
    amount
    description
    transferId
    receiptPhoto {
      id
      url
      contentType
    }
    project {
      id
      name
    }
    account {
      id
      name
    }
    category {
      id
      name
    }
  }
`);

export const OperationsDocument = graphql(`
  query Operations(
    $filter: FinancialOperationFilter
    $orderBy: [FinancialOperationOrderBy!]
    $first: Int!
  ) {
    financialOperations(filter: $filter, orderBy: $orderBy, first: $first) {
      totalCount
      nodes {
        ...OperationRow
      }
    }
  }
`);

export const OperationDocument = graphql(`
  query Operation($id: UUID!) {
    financialOperation(id: $id) {
      id
      date
      type
      amount
      description
      projectId
      accountId
      categoryId
      counterpartyId
      debtId
      transferId
      source
      needsReview
      reviewNote
      receiptPhotoId
      receiptPhoto {
        id
        url
        contentType
      }
      project {
        id
        name
        status
      }
      category {
        name
      }
      counterparty {
        name
      }
    }
  }
`);

export const CreateOperationDocument = graphql(`
  mutation CreateOperation($operation: FinancialOperationInput!) {
    createFinancialOperation(input: { financialOperation: $operation }) {
      financialOperation {
        id
      }
    }
  }
`);

export const UpdateOperationDocument = graphql(`
  mutation UpdateOperation($id: UUID!, $patch: FinancialOperationPatch!) {
    updateFinancialOperation(input: { id: $id, patch: $patch }) {
      financialOperation {
        id
        date
        type
        amount
        description
        projectId
        accountId
        receiptPhotoId
      }
    }
  }
`);

export const DeleteOperationDocument = graphql(`
  mutation DeleteOperation($id: UUID!) {
    deleteFinancialOperation(input: { id: $id }) {
      financialOperation {
        id
      }
    }
  }
`);

export const CreateTransferDocument = graphql(`
  mutation CreateTransfer($transfer: AccountTransferInput!) {
    createAccountTransfer(input: { accountTransfer: $transfer }) {
      accountTransfer {
        id
      }
    }
  }
`);

/** The treasurer roles write the finances; the committee reads them. */
export function useCanWrite(): boolean {
  return useSession().roles.includes('treasurer');
}

/** Why an operation is not corrected on the phone, or null. */
export function phoneLock(op: {
  transferId: string | null;
  debtId: string | null;
  source: string;
  project: { status: string } | null;
}): string | null {
  if (op.transferId) {
    return 'هذه العملية جزء من إيداع أو سحب: تُصحَّح من الحاسوب.';
  }
  if (op.debtId) return 'هذه العملية من قرض: تُصحَّح من الحاسوب.';
  if (op.source === 'bank_statement') {
    return 'عملية من كشف بنكي: تُصحَّح من الحاسوب.';
  }
  if (op.source === 'ledger_import') {
    return 'عملية من الدفاتر القديمة: تُصحَّح من الحاسوب.';
  }
  if (op.project?.status === 'CLOSED') {
    return 'المشروع مغلق: عملياته لا تُعدَّل.';
  }
  return null;
}

/** The project chosen last on this phone, proposed next time. */
const LAST_PROJECT = 'finance:lastProject';

export function lastProject(): string | null {
  try {
    return localStorage.getItem(LAST_PROJECT);
  } catch {
    return null;
  }
}

export function rememberProject(id: string) {
  try {
    localStorage.setItem(LAST_PROJECT, id);
  } catch {
    // Private mode or storage blocked: nothing is remembered.
  }
}
